import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getHelpdeskRatings, cleanupUncompletedRatings, markRatingsAsSeen } from '../../../models/helpdeskModel';

const PAGE_SIZES = [25, 50, 100];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const ratingBadgeClass = (rating) => {
  if (rating === null || rating === undefined) {
    return 'bg-amber-100 text-amber-700';
  }

  if (rating >= 8) return 'bg-emerald-100 text-emerald-800';
  if (rating >= 6) return 'bg-blue-100 text-blue-800';
  if (rating >= 4) return 'bg-yellow-100 text-yellow-800';
  return 'bg-rose-100 text-rose-800';
};

const HelpdeskRatings = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    status: 'rated',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [ratings, setRatings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);
  const [cleanupLoading, setCleanupLoading] = useState(false);

  useEffect(() => {
    fetchRatings();
    // Mark ratings as seen and refresh notifications
    markAllRatingsAsSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, filters.start_date, filters.end_date]);

  const markAllRatingsAsSeen = async () => {
    try {
      await markRatingsAsSeen();
      // Trigger a refresh of notification counts in navbar
      window.dispatchEvent(new Event('refreshNotifications'));
    } catch (error) {
      console.error('Failed to mark ratings as seen', error);
      // Don't show error to user as this is a background operation
    }
  };

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const payload = {
        page,
        per_page: perPage,
        status: 'rated'
      };

      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'status') return;
        if (value) {
          payload[key] = value;
        }
      });

      if (filters.search.trim()) {
        payload.search = filters.search.trim();
      }

      const response = await getHelpdeskRatings(payload);
      const payloadData = response?.data || response;
      setRatings(payloadData.ratings || []);
      setSummary(payloadData.summary || null);
      setPagination(payloadData.pagination || { total: 0, total_pages: 1 });
    } catch (error) {
      console.error('Failed to load ratings', error);
      toast.error(error?.response?.data?.message || 'Failed to load helpdesk ratings');
      setRatings([]);
      setSummary(null);
      setPagination({ total: 0, total_pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const totalPages = useMemo(() => pagination.total_pages || 1, [pagination]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    fetchRatings();
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'rated',
      search: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
  };

  const handleCleanupClick = () => {
    setShowCleanupModal(true);
  };

  const handleCleanupConfirm = async () => {
    try {
      setCleanupLoading(true);
      const response = await cleanupUncompletedRatings(cleanupDays);
      const data = response?.data || response;
      
      toast.success(data.message || `Cleaned up ${data.deleted_count} uncompleted ratings`);
      setShowCleanupModal(false);
      fetchRatings(); // Refresh the list
    } catch (error) {
      console.error('Failed to cleanup ratings', error);
      toast.error(error?.response?.data?.message || 'Failed to cleanup uncompleted ratings');
    } finally {
      setCleanupLoading(false);
    }
  };

  const visibleRatings = useMemo(
    () => ratings.filter((rating) => rating.rating !== null && rating.rating !== undefined),
    [ratings]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Professional Header Card with Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          {/* Header Row */}
          <div className="flex items-start justify-between mb-4">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Helpdesk Ratings</h1>
                <p className="text-xs text-gray-500 mb-1">Track customer satisfaction and submitted feedback</p>
                <div className="flex items-center mt-1 gap-4">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {summary?.rated_count ?? 0} rating{(summary?.rated_count ?? 0) !== 1 ? 's' : ''} received
                  </span>
                  <span className="text-xs text-gray-500">
                    Avg: {summary?.average_rating ? summary.average_rating.toFixed(1) : '—'}/10
                  </span>
                  <span className="text-xs text-gray-500">
                    Response rate: {summary?.response_rate ?? 0}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCleanupClick}
                className="inline-flex items-center justify-center px-3 py-1.5 text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 transition-all duration-200"
                title="Clean up old uncompleted rating requests"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Cleanup
              </button>
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={fetchRatings}
                disabled={loading}
                className="inline-flex items-center justify-center px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                style={{ 
                  backgroundColor: '#0087D4', 
                  boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Created From</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Created To</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Ticket #, requester, comment…"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="w-full px-4 py-1.5 text-white text-sm font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                style={{ 
                  backgroundColor: '#0087D4', 
                  boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' 
                }}
              >
                Search
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
              <button
                type="button"
                onClick={handleClearFilters}
                className="w-full px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Ratings Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3">Ticket</th>
                <th className="px-4 py-3">Requester</th>
                <th className="px-4 py-3">Assigned</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Rating</th>
                <th className="px-4 py-3">Comment</th>
                <th className="px-4 py-3">Requested</th>
                <th className="px-4 py-3">Rated</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
              {visibleRatings.length === 0 && !loading && (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-gray-500">
                    No rated tickets available for the selected filters.
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-gray-500">
                    Loading ratings…
                  </td>
                </tr>
              )}
              {!loading &&
                visibleRatings.map((rating) => (
                  <tr key={rating.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{rating.ticket_number || `#${rating.ticket_id}`}</div>
                      <div className="text-xs text-gray-500">{rating.title}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div>{rating.requester_name || '—'}</div>
                      <div className="text-xs text-gray-500">{rating.requester_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {rating.assigned_to ? (
                        <div className="text-gray-900">{rating.assigned_to}</div>
                      ) : (
                        <span className="text-gray-400">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{rating.category_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${ratingBadgeClass(rating.rating)}`}>
                        {rating.rating !== null && rating.rating !== undefined ? `${rating.rating}/10` : '—'}
                      </span>
                      <div className="text-xs text-gray-400 mt-1 capitalize">{rating.rating_status}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {rating.comment ? (
                        <p className="text-gray-700 truncate" title={rating.comment}>
                          {rating.comment}
                        </p>
                      ) : (
                        <span className="text-gray-400">No comment</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{formatDateTime(rating.created_at)}</td>
                    <td className="px-4 py-3">{formatDateTime(rating.rated_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/helpdesk?ticket=${rating.ticket_id}`)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                      >
                        View Ticket
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600 gap-2">
          <div>
            Showing{' '}
            <span className="font-semibold">
              {visibleRatings.length ? (page - 1) * perPage + 1 : 0}-
              {Math.min(page * perPage, pagination.total || 0)}
            </span>{' '}
            of <span className="font-semibold">{pagination.total || 0}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Cleanup Modal */}
      {showCleanupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Clean Up Uncompleted Ratings</h3>
                <button
                  onClick={() => setShowCleanupModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={cleanupLoading}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  This will permanently delete rating requests that were sent but never completed by users.
                  {summary?.pending_count > 0 && (
                    <span className="block mt-2 font-medium text-amber-700">
                      Currently {summary.pending_count} pending rating(s) in the system.
                    </span>
                  )}
                </p>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delete requests older than:
                </label>
                <select
                  value={cleanupDays}
                  onChange={(e) => setCleanupDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  disabled={cleanupLoading}
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCleanupModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={cleanupLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCleanupConfirm}
                  disabled={cleanupLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
                >
                  {cleanupLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Cleaning...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clean Up
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default HelpdeskRatings;
